import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { FireIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ResetIcon from 'components/icons/reset-icon';
import Text from 'components/inputs/text';
import GridDraggableItem from 'components/structure-page/2d/grid-draggable-item';
import { addApplianceToSpace } from 'features/structure';
import { useState } from 'react';
import {
  filterAppliancesBySearchText,
  getAdvancedAppliances,
  getSignificantAppliances
} from 'utils/appliances';
import { errorModal } from 'utils/ui';

export default function ApplianceList() {
  const dispatch = useAppDispatch();
  const { applianceItems } = useAppSelector((state) => state.data);
  const { estimatingStructure } = useAppSelector((state) => state.ui);
  const { activeItem } = useAppSelector((state) => state.ui);
  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const filteredAppliances = filterAppliancesBySearchText(
    getSignificantAppliances(applianceItems),
    searchText
  );
  const filteredAdvancedAppliances = filterAppliancesBySearchText(
    getAdvancedAppliances(applianceItems),
    searchText
  );
  const advancedExpanded = showAdvanced || searchText.length > 0;

  const handleAddAppliance = (itemId: number) => {
    if (!activeItem?.uniqueId) {
      errorModal('Please select a space first to add appliances');
      return;
    }
    dispatch(
      addApplianceToSpace({
        spaceId: activeItem.uniqueId,
        applianceItemId: itemId
      })
    );
  };

  const renderAppliance = (item: (typeof applianceItems)[number]) => (
    <GridDraggableItem
      key={item.itemId}
      id={`appliance-${item.itemId}`}
      label={item.label}
      type='new-appliance'
      itemId={item.itemId}
      disabled={estimatingStructure}
      onDoubleClick={() => handleAddAppliance(item.itemId)}
      color='purple'
    />
  );

  return (
    <div className='flex w-full grow flex-col gap-2 overflow-hidden'>
      <div className='flex items-center gap-1'>
        <FireIcon className='h-6 w-6 shrink-0' />
        {searching ? (
          <Text
            value={searchText}
            onInput={setSearchText}
            onBlur={() => setSearching(false)}
            autoFocus
            autoSelect
            size='sm'
            placeholder='Search...'
          />
        ) : (
          <>
            <span className='grow text-lg font-semibold'>
              Appliances{searchText.length > 0 && ' (Filtered)'}
            </span>
            {searchText.length > 0 ? (
              <ResetIcon
                className='h-7 w-7 shrink-0 cursor-pointer rounded-md p-[3px] hover:bg-gray-100'
                onClick={() => setSearchText('')}
              />
            ) : (
              <MagnifyingGlassIcon
                className='h-7 w-7 shrink-0 cursor-pointer rounded-md p-[3px] hover:bg-gray-100'
                onClick={() => setSearching(true)}
              />
            )}
          </>
        )}
      </div>
      <div className='flex grow flex-col gap-1 overflow-auto'>
        {filteredAppliances.map(renderAppliance)}
        {advancedExpanded && filteredAdvancedAppliances.map(renderAppliance)}
        {filteredAdvancedAppliances.length > 0 && searchText.length === 0 && (
          <button
            className='mt-1 flex cursor-pointer items-center justify-center gap-1 self-center text-sm font-medium text-gray-500 hover:text-gray-700'
            type='button'
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            <span>{advancedExpanded ? 'Show Less' : 'Show More'}</span>
            {advancedExpanded ? (
              <ChevronUpIcon className='h-5 w-5' />
            ) : (
              <ChevronDownIcon className='h-5 w-5' />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
